import type { ClientTimeEntry, MyTimeEntry } from '../api/time.ts';
import { addDays } from './days.ts';

/**
 * The time screens' pure rules (money plan phase 1, 161), kept out of the components so the tests read them as they run.
 */

export type ClientPreset = 'year' | 'month' | 'quarter';
export const CLIENT_PRESETS: { key: ClientPreset; label: string }[] = [
  { key: 'year', label: 'Last 12 months' }, { key: 'quarter', label: 'Last 3 months' }, { key: 'month', label: 'This month' },
];

/** A client preset's days, from the agency's today; the 12 months are the server's default (null). */
export function presetRange(p: ClientPreset, today: string): { from: string; to: string } | null {
  if (p === 'year') return null;
  const first = `${today.slice(0, 8)}01`;
  if (p === 'month') return { from: first, to: today };
  const [y, m] = first.split('-').map(Number) as [number, number];
  return { from: new Date(Date.UTC(y, m - 1 - 2, 1)).toISOString().slice(0, 10), to: today };
}

/** A client's entries under each day's heading (they arrive newest day first), with the day's exact total. */
export function byDay(entries: ClientTimeEntry[]): { day: string; minutes: number; entries: ClientTimeEntry[] }[] {
  const out: { day: string; minutes: number; entries: ClientTimeEntry[] }[] = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (last && last.day === e.on_date) { last.entries.push(e); last.minutes += e.minutes; }
    else out.push({ day: e.on_date, minutes: e.minutes, entries: [e] });
  }
  return out;
}

/** How a My time entry's client reads: named only where the reader still sees it (Q8). */
export function clientWords(c: MyTimeEntry['client']): string {
  if (c === null) return 'No client';
  if (c === 'hidden') return 'A client you no longer see';
  return [c.ref, c.name].filter(Boolean).join(' ');
}

export type DepartmentRange = 'week' | 'month' | 'year';
/** A by-client range from the agency's today: this week (the server's default, null), the last 31 days, the last 366. */
export function clientRange(r: DepartmentRange, today: string): { from: string; to: string } | null {
  if (r === 'week') return null;
  return { from: addDays(today, r === 'month' ? -30 : -365), to: today };
}
